import { api } from '@/lib/api';
import { emit } from '@/lib/analytics';

export interface BriefingItem {
  id: string;
  category: 'deals' | 'clients' | 'revenue' | 'actions';
  priority: 'high' | 'medium' | 'low';
  message: string;
  actionLabel?: string;
  actionTarget?: string;
  /** What the item is about (signal, renewal, churn_risk, …); the API stamps it from the id. */
  kind?: string;
  /** The workspace's own model's chance this item is opened; present only when the API ordered the briefing by it. */
  openLikelihood?: number;
}

export interface MlReadiness {
  modelsWithData: number;
  totalCoreModels: number;
  /** 'training' / 'cold_start' are what the API sent before 2026-10; still accepted. */
  status: 'ready' | 'partial' | 'no_live_model' | 'training' | 'cold_start';
}

export interface MorningBriefing {
  greeting: string;
  date: string;
  items: BriefingItem[];
  summary: string;
  /** How many core prediction tables have data — drives the "models warming up" banner. */
  mlReadiness?: MlReadiness;
  /** items = something to say; quiet = a real check found nothing (the summary says what was checked); no_data = nothing connected. */
  status?: 'items' | 'quiet' | 'no_data';
  /** When the newest finished sync the briefing was built on completed (ISO), or null. */
  dataAsOf?: string | null;
  /** stored = today's persisted briefing (the one emailed); live = built for this request. */
  source?: 'stored' | 'live';
  /** The written summary, after the API's coverage rule; only on a stored briefing for an org-wide viewer. */
  narrative?: { decisions: string[]; risks: string[]; shipped: string[]; meetings: string[] };
  /** One line when the written summary is missing (AI limit reached, model unavailable). */
  narrativeNotice?: string;
  /** The stored briefing's id; absent on a live build, which was never delivered. */
  briefingId?: string;
}

/**
 * One briefing item opened (clicked through) or dismissed: the label the API's
 * briefing_priority model learns from. Only a stored briefing has an id; a live
 * build was never delivered, so there is nothing to join the event to.
 */
export function emitBriefingItem(name: 'briefing_item_opened' | 'briefing_item_dismissed', briefingId: string | undefined, item: BriefingItem): void {
  if (!briefingId) return;
  emit(name, { briefing_id: briefingId, item_id: item.id, item_kind: item.kind ?? null });
}

/**
 * Throws when the API fails, so the screen shows a retryable error. It used to swallow the
 * failure into an "empty" briefing that told the owner to "Connect your integrations": a
 * 5xx on a fully connected workspace read as "nothing connected" (four-state pass, 2026-10).
 */
export async function getMorningBriefing(): Promise<MorningBriefing> {
  return api<MorningBriefing>('/v1/morning-briefing');
}